import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import VideoPlayer from './VideoPlayer';
import { useSelector } from 'react-redux';

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const VideoListBlock = styled.div`
  overflow: hidden;
  width: 100%;
  padding: 0;

  @media (max-width: ${breakpoints.phone}px) {
    margin-bottom: 16px;
  }
`;

const VideoListMenu = styled.div`
  margin-bottom: 20px;
`;

const SectionHeader = styled.div`
  font-size: 1rem;
  font-weight: bold;
  padding: 10px;
  margin-bottom: 0;
  border-radius: 5px 5px 0 0;
  color: white;
  background-color: ${({ colors }) => colors.componentHeaderBackground};

  @media (max-width: ${breakpoints.phone}px) {
    text-align: center;
    padding: 8px;
  }
`;

const ResourceList = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
  list-style: none;
  padding: 20px;
  border-radius: 0 0 5px 5px;
  background-color: ${({ colors }) => colors.cardBackground};
  margin-top: 0;

  @media (max-width: ${breakpoints.phone}px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 15px;
  }
`;

const ResourceItem = styled.li`
  background-color: ${({ colors }) => colors.buttonBackground};
  border-radius: 5px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
  }

  a {
    display: block;
    color: #0073e6;
    text-decoration: none;
    font-size: 12px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  p {
    padding: 12px 8px;
    font-size: 15px;
    color: ${({ colors }) => colors.buttonText};
    text-align: center;
    font-weight: bold;
    cursor: pointer;
  }
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  border-radius: 5px 5px 0 0;
  overflow: hidden;
`;

const ThumbnailImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PlayIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background-color: rgba(12, 43, 63, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 10px 0 10px 16px;
    border-color: transparent transparent transparent #fff;
    margin-left: 3px;
  }

  ${ThumbnailWrapper}:hover & {
    background-color: rgba(25, 81, 116, 0.9);
  }
`;

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: '600px',
    padding: '0',
  },
};


const EmptyState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  color: #555;
  font-size: 14px;
`;

const SubscriptionVideosListing = ({ colors, genre }) => {
  const subscriptionVideosData = useSelector(
    (state) => state.auth.subscription_videos
  );
  const isValidCollection =
    subscriptionVideosData &&
    typeof subscriptionVideosData === 'object' &&
    !Array.isArray(subscriptionVideosData);
  const videosByGenre = isValidCollection ? subscriptionVideosData : {};
  const videos = genre && videosByGenre[genre] ? videosByGenre[genre] : [];
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);

  const openModal = (video) => {
    setCurrentVideo(video);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setCurrentVideo(null);
  };

  return (
    <VideoListBlock>
      <VideoListMenu>
        <SectionHeader colors={colors}>Subscription Videos </SectionHeader>
        <ResourceList colors={colors}>
          {videos?.length ? (
            videos.map((video, index) => (
              <ResourceItem key={`${genre}-${index}`} colors={colors}>
                <a onClick={() => openModal(video)}>
                  <ThumbnailWrapper>
                    <ThumbnailImage src={video.thumbnail} alt={video.title} />
                    <PlayIcon />
                  </ThumbnailWrapper>
                  <p>{video.title}</p>
                </a>
              </ResourceItem>
            ))
          ) : (
            <EmptyState>
              {genre
                ? `No videos available for ${genre}.`
                : 'Select a category to see available videos.'}
            </EmptyState>
          )}
        </ResourceList>
      </VideoListMenu>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Video Player"
        ariaHideApp={false}
      >
        {currentVideo && (
          <VideoPlayer
            videoLink={currentVideo.video}
            title={currentVideo.title}
            closeModal={closeModal}
            colors={colors}
          />
        )}
      </Modal>
    </VideoListBlock>
  );
};

export default SubscriptionVideosListing;
